import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';

// Управление Badge API на иконке PWA (Android Chrome / Desktop Chrome)
// iOS Badge API не поддерживает — безопасно игнорируем
function updateAppBadge(count) {
  if (!('setAppBadge' in navigator)) return;
  if (count > 0) {
    navigator.setAppBadge(count).catch(() => {});
  } else {
    navigator.clearAppBadge().catch(() => {});
  }
}

// ⚠️ Android: Badging API в Chrome на Android НЕ поддерживается — цифру на иконке
// рисует лаунчер (Samsung и др.) по числу системных уведомлений приложения в
// шторке. Прочитал в приложении (Канал, Центр) — а уведомления висят в шторке,
// и цифра не сбрасывается. Поэтому закрываем системные уведомления, чей url
// совпадает с уже ПРОЧИТАННОЙ записью Центра и нет непрочитанной с тем же url.
// Уведомления с url, которого нет в списке Центра (тест, события без записи в
// Центре, старые за пределами выборки), не трогаем — консервативно.
// На iOS/десктопе это тоже убирает прочитанное из Центра уведомлений ОС.
async function closeReadSystemNotifications(list) {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || typeof reg.getNotifications !== 'function') return;
    const shown = await reg.getNotifications();
    if (!shown.length) return;
    const unreadUrls = new Set();
    const readUrls = new Set();
    for (const n of list) {
      if (!n.url) continue;
      (n.read_at ? readUrls : unreadUrls).add(n.url);
    }
    for (const sn of shown) {
      const url = sn.data && sn.data.url;
      if (url && readUrls.has(url) && !unreadUrls.has(url)) sn.close();
    }
  } catch { /* getNotifications недоступен — бейдж просто не синхронизируется */ }
}

// Считаем unread из дедуплицированного списка (одно событие = одно непрочитанное)
function countUnread(notifications) {
  const seen = new Set();
  let count = 0;
  for (const n of notifications) {
    if (n.read_at) continue;
    const minute = Math.floor(new Date(n.sent_at).getTime() / 60000);
    const key = `${n.event_type}|${n.url || ''}|${minute}`;
    if (!seen.has(key)) { seen.add(key); count++; }
  }
  return count;
}

const POLL_INTERVAL = 30_000; // 30 секунд

export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const fetch = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.getNotifications();
      const list = data.notifications || [];
      const count = countUnread(list);
      setNotifications(list);
      setUnread(count);
      updateAppBadge(count);
      closeReadSystemNotifications(list);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.markNotificationsRead();
      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        closeReadSystemNotifications(updated);
        return updated;
      });
      setUnread(0);
      updateAppBadge(0);
    } catch {}
  }, []);

  // ids — массив id (группа дедуплицированных записей одного события)
  const markOneRead = useCallback(async (ids) => {
    const idList = Array.isArray(ids) ? ids : [ids];
    try {
      await Promise.all(idList.map(id => api.markNotificationRead(id)));
      const now = new Date().toISOString();
      setNotifications(prev => {
        const updated = prev.map(n => idList.includes(n.id) ? { ...n, read_at: n.read_at || now } : n);
        const count = countUnread(updated);
        setUnread(count);
        updateAppBadge(count);
        closeReadSystemNotifications(updated);
        return updated;
      });
    } catch {}
  }, []);

  useEffect(() => {
    fetch();
    timerRef.current = setInterval(() => fetch(true), POLL_INTERVAL);
    // Мгновенный refresh по требованию (демо-тур слайс 2: оффер инвойса / оплата)
    const onRefresh = () => fetch(true);
    window.addEventListener('notifications-refresh', onRefresh);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener('notifications-refresh', onRefresh);
    };
  }, [fetch]);

  return { notifications, unread, loading, refresh: fetch, markAllRead, markOneRead };
}
